import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { subContractService } from '../services';
import { dynamicSort } from '../utils';
import {
  fetchSubcontractsRequested,
  fetchSubcontractsSucceeded,
  fetchSubcontractsFailed,
  getAllSubcontractsSelector,
  getSubcontractsLoadingSelector,
  getSubcontractsErrorSelector
} from '../redux/slices/subcontractSlice';
import { trackingEvent } from '../analytics/index';
import { AMPLITUDE_SERVICE } from '../analytics/constants';
import { getBasicAmplitudEventProperties } from '../analytics/utils';

/**
 * Custom hook for managing subcontracts with Redux state management
 */
export const useSubcontracts = () => {
  const dispatch = useDispatch();
  const projectState = useSelector((state) => state.projectState);
  const stateCompany = useSelector((state) => state.companyState);

  // Redux state selectors
  const subcontracts = useSelector(getAllSubcontractsSelector);
  const loading = useSelector(getSubcontractsLoadingSelector);
  const error = useSelector(getSubcontractsErrorSelector);

  /**
   * Basic function that only fetches subcontracts and dispatches to Redux
   */
  const fetchSubcontracts = useCallback(
    async (projectId) => {
      if (!projectId) {
        const error = 'No project ID provided';
        dispatch(fetchSubcontractsFailed(error));
        return null;
      }

      dispatch(fetchSubcontractsRequested());

      try {
        const subcontractsGet =
          await subContractService.getByProject(projectId);

        if (!subcontractsGet) {
          dispatch(fetchSubcontractsFailed('No subcontracts data received'));
          return [];
        }

        const subContractsOrdered = subcontractsGet.subcontracts.sort(
          dynamicSort('name')
        );

        // Update Redux state
        dispatch(fetchSubcontractsSucceeded(subContractsOrdered));

        return subContractsOrdered;
      } catch (err) {
        const errorMessage = err.message || 'Failed to fetch subcontracts';
        dispatch(fetchSubcontractsFailed(errorMessage));
        return null;
      }
    },
    [dispatch]
  );

  /**
   * Fetches subcontracts by project and handles Gantt integration
   */
  const fetchSubcontractsByProject = useCallback(async () => {
    const projectId = projectState.projectSelected;

    if (!projectId) {
      const error = 'No project selected';
      dispatch(fetchSubcontractsFailed(error));
      return null;
    }

    // Use the basic fetch function
    const subContractsOrdered = await fetchSubcontracts(projectId);

    if (subContractsOrdered) {
      // Handle Gantt integration
      if (window.to_use_react_gantt) {
        window.to_use_react_gantt.subContracts = subContractsOrdered;
        const idToAssignLastSubcontract =
          window.to_use_react_gantt.toHardAssignSubcontract;

        if (
          idToAssignLastSubcontract &&
          window.to_use_react_gantt.isTaskExists(idToAssignLastSubcontract)
        ) {
          const objectTask = window.to_use_react_gantt.getTask(
            idToAssignLastSubcontract
          );
          let lastId = 0;

          window.to_use_react_gantt.subContracts.forEach((s) => {
            if (s.id > lastId) lastId = s.id;
          });

          objectTask.subcontractId = lastId;

          trackingEvent(
            'activity_company_entry',
            {
              ...getBasicAmplitudEventProperties(),
              activity_name: objectTask?.text,
              activity_id: objectTask?.id,
              activity_index: objectTask?.correlative_id
            },
            AMPLITUDE_SERVICE
          );

          delete window.to_use_react_gantt.toHardAssignSubcontract;
        }
        window.to_use_react_gantt.changeVisualizationOption();
        window.to_use_react_gantt.refreshData();
      }
    }

    return subContractsOrdered;
  }, [projectState.projectSelected, fetchSubcontracts, dispatch]);

  /**
   * Simplified function to fetch subcontracts by company - only returns data
   * @param {string} companyId - Company ID to fetch subcontracts for
   * @returns {Promise<Array>} Array of subcontracts or empty array on error
   */
  const fetchSubcontractsByCompany = useCallback(
    async (companyId = null) => {
      try {
        const idCurrentCompany = companyId || stateCompany.currentCompany?.id;
        if (!idCurrentCompany) {
          return [];
        }

        const subcontractsGet =
          await subContractService.getByCompany(idCurrentCompany);

        if (subcontractsGet && subcontractsGet.subcontracts) {
          return subcontractsGet.subcontracts.sort(dynamicSort('name'));
        }

        return [];
      } catch (err) {
        return [];
      }
    },
    [stateCompany.currentCompany?.id]
  );

  return {
    fetchSubcontracts,
    fetchSubcontractsByProject,
    fetchSubcontractsByCompany,
    subcontracts,
    loading,
    error
  };
};
